import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Refetches `/auth/me/capabilities` when the authenticated user changes route or
 * returns to the app (tab visibility / window focus) so plan usage stays current
 * without a full page reload.
 */
export function useCapabilitiesRefetch() {
  const { refreshCapabilities, isAuthenticated } = useAuth();
  const location = useLocation();
  const skipNextPathRefetch = useRef(true);
  const lastReturnRefetchAt = useRef(0);
  const tabWasHiddenRef = useRef(false);
  const windowHadBlurRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      skipNextPathRefetch.current = true;
      return;
    }
    if (skipNextPathRefetch.current) {
      skipNextPathRefetch.current = false;
      return;
    }
    void refreshCapabilities();
  }, [isAuthenticated, location.pathname, location.search, refreshCapabilities]);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const maybeRefetch = () => {
      const now = Date.now();
      if (now - lastReturnRefetchAt.current < 800) return;
      lastReturnRefetchAt.current = now;
      void refreshCapabilities();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        tabWasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === 'visible' && tabWasHiddenRef.current) {
        tabWasHiddenRef.current = false;
        maybeRefetch();
      }
    };

    const onWindowBlur = () => {
      windowHadBlurRef.current = true;
    };
    const onWindowFocus = () => {
      if (!windowHadBlurRef.current) return;
      windowHadBlurRef.current = false;
      maybeRefetch();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('focus', onWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [isAuthenticated, refreshCapabilities]);
}
